import { ValueAndUnit } from "../types";
import { State } from "../status/Types";


export enum StatesToDisplayItemFieldTypes {
    CRITICAL = "CRITICAL",
    MAIN_METRIC = "MAIN_METRIC",
    INFORMATION = "INFORMATION",
}

/**
 * Structure of an item of the StatesToDisplay state as it comes from the controller.
 * DisplayName, DisplayNameShort is not localized.
 * @see PoolDevicePropertyToDisplay
 */
export type StatesToDisplayItem = {
    /** Name of the state (PoolsideDaemonName) E.g: DesriedSpeedPercent */
    Name: string,
    /** English language name as it comes from the Controller */
    DisplayName: string,
    /** English language name as it comes from the Controller */
    DisplayNameShort: string,
    DisplayOrder: number,
    DisplayProcessingLogic: DisplayProcessingLogics,
    DisplayProcessingLogicUnitAsDisplayName: boolean,
    /** Allow displaying it even when the device is off.
     * Most device does not provide up to date information in case it's OnOff state is off so better not to show them.
     * But e.g: raypack AVIA heaters reports up to date info even when it's OnOff state is off. For this state DisplayWhenOff is set to true.
     */
    DisplayWhenOff?: boolean,
    /** normal, wide, full */
    DisplayWidth?: DisplayWidths,
    DisplayDiffTimeout?: number,
    DisplayDiffThreshold?: number,
    FieldTypes?: StatesToDisplayItemFieldTypes[]
}

/** Possible values of the DeviceStatesToDisplay state */
export enum DisplayProcessingLogics {
    AMP = "AMP",
    DATETIME = "DATETIME",
    MG_L = "MG_L",
    ONOFF = "ONOFF",
    OPEN_CLOSE = "OPEN_CLOSE",
    TEMP_F = "TEMP_F",
    TWINKLE="TWINKLE",
    GPM = "GPM",
    HVAC_MODE = "HVAC_MODE",
    PPM = "PPM",
    PSI = "PSI",
    RPM = "RPM",
    MS_TO_S = "MS_TO_S",
    MV = "MV",
    BOOLEAN = "BOOLEAN",
    FLOAT = "FLOAT",
    INTEGER = "INTEGER",
    STRING = "STRING",
    LIGHT_NAME = "LIGHT_NAME",
    LONG_STRING = "LONG_STRING",
    PERCENT = "PERCENT",
    ACTUATOR_FRIENDLY_STATE = "ACTUATOR_FRIENDLY_STATE",
    ACTUATOR_POSITION = "ACTUATOR_POSITION",
    HEATER_FRIENDLY_STATE = "HEATER_FRIENDLY_STATE",
    RUNNINGIDLE = "RUNNINGIDLE",
    UA = "UA",
    VOLT = "VOLT",
    WATER_SENSOR = "WATER_SENSOR",
    WATTAGE = "WATTAGE",
    X = "X",
    HEATPUMP_FRIENDLY_STATE = "HEATPUMP_FRIENDLY_STATE",
    HEATPUMP_OPERATING_MODE = "HEATPUMP_OPERATING_MODE",
    HEATPUMP_CAPABILITY = "HEATPUMP_CAPABILITY",
}

/** Device realted common state names according used by the Controller. */
export enum PoolsideDaemonDeviceStateNamesCommon {
    InformationFields = "InformationFields",
    ErrorState = "ErrorState",
    Winterized = "Winterized",
    AutoFilterVersion = "AutoFilterVersion",
}

export enum PoolsideDaemonDeviceStateValues {
    ON = "ON",
    OFF = "OFF",
    UNKNOWN = "UNKNOWN",
}

export class PoolDeviceDataToDisplay {
    CriticalFields: PoolDeviceCriticalFieldPropertyAndValue[];
    /* Array of Critical Field Identifiers */
    CriticalFieldsDiff: string[];
    MainMetricField: PoolDevicePropertyAndValue;
    InformationFields: PoolDevicePropertyAndValue[];
}

export class PoolDeviceCriticalFieldPropertyAndValue {
    Identifier: string;
    ActualPropertyAndValue: PoolDevicePropertyAndValue;
    DesiredPropertyAndValue: PoolDevicePropertyAndValue;
    LocalDesiredPropertyAndValue: PoolDevicePropertyAndValue;
}

export class PoolDevicePropertyAndValue {
    Property: PoolDevicePropertyToDisplay;
    Value: any;
    ValueAndUnit: ValueAndUnit;
    ValueFormatted: string;
    Updated: Date
}

export type PoolDeviceCriticalDesiredActual = {
    /** Name of the actual/desired state pair.
     * E.g:
     * PowerState for ActualPowerState / PowerState
     * SpeedPercent for ActualSpeedPercent / DesiredSpeedPercent
     * GPM for GPM / DesiredGPM */
    Identifier: string;
    DesiredProperty: PoolDevicePropertyToDisplay,
    ActualProperty: PoolDevicePropertyToDisplay,
    // Planned.
    // FeldType or UIElement or someting to specify/link UI element for the property
    // FieldType: "POWER" | "SPEED" | "LIGHTNAME" 
}

/**
 * Localised version of StatesToDisplay
 * @see StatesToDisplay
 */
export type PoolDevicePropertyToDisplay = {
    /** Name of the state (PoolsideDaemonName) E.g: DesiredPowerState */
    Name: string,
    /** Localised */
    DisplayName: string,
    /** Localised */
    DisplayNameShort: string,
    DisplayOrder: number,
    DisplayProcessingLogic: DisplayProcessingLogics,
    DisplayProcessingLogicUnitAsDisplayName: boolean,
    DisplayWhenOff?: boolean,
    DisplayWidth?: DisplayWidths,
    DisplayDiffTimeout?: number,
    DisplayDiffThreshold?: number,
}

export enum DisplayWidths {
    NORMAL = "NORMAL",
    HALF = "HALF",
    WIDE = "WIDE",
    FULL = "FULL",
}

export type StateMap = {
    [ name: string ]: State
};

/**
 * Internal format of state.
 * Value is JSONDecoded if needed. "true"  converted to true, "false" to false, "123" into 123 etc.
 */
export type DeviceState = {
    value: any,
    updated: Date
}

export enum criticalFieldSummaryModes {
    /** Always show the full status summary, E.g: On - Red - 95% - 2x */
    FULL = "FULL",
    /** Show only the on/off state if actual on/off state is OFF. Show full state summmary */
    FULL_IF_ON_OTHERWISE_ONOFF_ONLY = "FULL_IF_ON_OTHERWISE_ONOFF_ONLY"
}
